import { useEffect, useState } from "react";
import { getJobRequirementById } from "@endpoints/hiringCompany/requirements";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import { colors } from "@utils/theme";
import { JobType, WorkPlaceOptions } from "@utils/constants";
import { getJobStatusLabel } from "@utils/helperFunction";
import { JobRequirement } from "@pages/hiringCompany/requirements/candidates/candidates.types";
import ChevronLeftIcon from "@assets/svg/ChevronLeftIcon";
import { Dropdown } from "@components/Dropdown/Dropdown";
import { useForm } from "react-hook-form";
import { Camera } from "lucide-react";
import { convertJobData } from "./RequirementDetails.utils";

const RequirementDetails: React.FC = () => {
  const params = useParams();
  const navigate = useNavigate();
  const [details, setDetails] = useState<JobRequirement>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const {
    control,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: {
      action: undefined,
    },
  });

  const fields = watch();

  useEffect(() => {
    fetchDetails();
  }, []);

  useEffect(() => {
    if (fields?.action && fields?.action === "Create job post") {
      createJob();
    }
  }, [fields.action]);

  const fetchDetails = async () => {
    try {
      const {success,data} = await getJobRequirementById({jobRequirementId:params?.id});
      if (success) {
        setDetails(data);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  console.log(details)

  if (isLoading) {
    return (
      <div className="flex flex-1 flex-col rounded-lg bg-white w-full flex-grow p-6 justify-center items-center">
        <LoadingAnimation color={colors.PrimaryBlack} />
      </div>
    );
  }

  const jobFields = [
    { label: "Job title", value: details?.title },
    { label: "Job reference", value: details?.refNo },
    { label: "Job location", value: details?.location },
    { label: "Industry type", value: details?.industryType },
    {
      label: "Work place",
      value: WorkPlaceOptions.find((x) => details?.workPlaceOption === x.value)
        ?.label,
    },
    {
      label: "Job type",
      value: JobType.find((x) => details?.jobType === x.value)?.label,
    },
    {
      label: "Salary Range",
      value: `£${details?.salary.min}${
        details?.salary.max && "-  £" + details?.salary.max
      }`,
    },
    {
      label: "Job published date",
      value: dayjs(details?.createdAt).format("Do MMMM YYYY"),
    },
    { label: "Job Status", value: getJobStatusLabel(details?.status) },
  ];

  const createJob = () => {
    const data = convertJobData(details);
    navigate("/job/create", { state: data });
  };

  return (
    <div className="flex flex-col rounded-lg bg-white w-full flex-grow p-6 ">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center self-start pb-5"
      >
        <ChevronLeftIcon className={"w-4 h-4"} fill="#4d4d4d" />
        <label className="text-xs font-medium text-primary cursor-pointer">
          Back to listing
        </label>
      </button>
      <div className="flex  items-center justify-between w-full gap-8 bg-[#F1F5F9] rounded-lg px-5 py-3 mb-3">
        <div className="flex flex-1 flex-col gap-2">
          <span className="text-xs text-primary">Company name :</span>
          <span className="text-sm text-primary font-semibold">
            {details?.company?.name} -{" "}
            <span className="text-sm text-primary font-normal">
              {details?.refNo}
            </span>
          </span>
        </div>
        <div className="flex justify-center items-center gap-2">
          <Dropdown
            name="action"
            control={control}
            rules={{ required: "This field is required." }}
            options={["Create job post", "Delete job"]}
            errors={errors}
            placeholder="Actions"
          />
          <div className="p-1 border border-gray-300 bg-white rounded">
            <Camera />
          </div>
        </div>
      </div>
      <div className="overflow-scroll no-scrollbar flex flex-1">
        <div className="flex  flex-col p-6 md:p-10 max-w-5xl mx-auto w-3/4 ">
          <div className="flex flex-col flex-1 gap-5  w-3/4 self-center">
            {jobFields.map((field, idx) => (
              <div key={idx} className="flex ">
                <span className="min-w-[160px] font-semibold text-xs">
                  {field.label} :
                </span>
                <span className="font-normal text-xs">{field.value}</span>
              </div>
            ))}
            <div className="flex items-start">
              <span className="min-w-[160px] font-semibold text-xs">
                Job Description :
              </span>
              <p
                className="font-normal text-xs"
                dangerouslySetInnerHTML={{ __html: details?.description }}
              />
            </div>
          </div>

          <hr className="my-8 border-t border-dashed border-gray-300" />
          <div className="flex flex-col w-3/4 self-center">
            <h3 className="font-semibold text-xs text-primary">
              Skills required :
            </h3>
            <div className="h-full flex flex-1 flex-col mt-3">
              {(details?.skills?.length ?? 0) > 0 && (
                <div className="grid space-x-3 py-2.5 items-center w-full justify-center relative grid-cols-[1.7fr_0.68fr_0.58fr_1fr]">
                  {["Skill", "Rating", "Years", "When", ""].map((label) => (
                    <span
                      key={label}
                      className="text-xs font-semibold text-gray-700 flex-1"
                    >
                      {label}
                    </span>
                  ))}
                </div>
              )}
              <div className="overflow-auto h-full w-full">
                {details?.skills?.map((skill) => (
                  <div
                    key={skill?.lastUsed}
                    className="grid space-x-3 py-2.5 items-center w-full justify-center relative grid-cols-[1.7fr_0.68fr_0.58fr_1fr]"
                  >
                    {[
                      skill.title,
                      `${skill?.rating}/10`,
                      skill.years,
                      skill.lastUsed,
                    ].map((value, index) => (
                      <div
                        key={index}
                        className="border border-gray-100 bg-[#FAFAFA] px-4 py-2.5 text-xs font-medium text-primary rounded flex flex-1"
                      >
                        {value}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequirementDetails;
